import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import {
  CheckCircle,
  XCircle,
  Buildings,
  EnvelopeSimple,
  User,
  Phone,
  Spinner,
  Handshake,
  CalendarCheck,
  Package,
  Lock,
  Eye,
  EyeSlash,
  SignIn,
  UserPlus,
} from '@phosphor-icons/react'
import api from '../services/api'
import { CNPJLookupInput } from '../components/cnpj'
import type { CNPJData } from '../services/brasilapi'

interface InviteDetails {
  id: string
  shipperName: string
  shipperTradingName?: string
  companyName?: string
  contactName?: string
  email: string
  expiresAt: string
}

interface ExistingUser {
  id: string
  name: string
  email: string
}

// Simplified view modes - we always treat user as logged out
type ViewMode = 'loading' | 'error' | 'login' | 'signup' | 'success'

export default function AcceptAgentInvitePage() {
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()

  const [loading, setLoading] = useState(false)
  const [loadingInvite, setLoadingInvite] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [invite, setInvite] = useState<InviteDetails | null>(null)
  const [existingUser, setExistingUser] = useState<ExistingUser | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('loading')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false)

  // Form state for signup
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    document: '',
    phone: '',
    password: '',
    confirmPassword: '',
  })

  // Form state for login
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  })

  // Load invite details - always ignoring auth state
  useEffect(() => {
    async function loadInvite() {
      if (!token) {
        setError('Token de convite invalido')
        setViewMode('error')
        setLoadingInvite(false)
        return
      }

      try {
        const response = await api.get(`/agent-invite/${token}`)
        const inviteData = response.data.invite
        const existingUserData = response.data.existingUser

        setInvite(inviteData)
        setExistingUser(existingUserData)

        // Pre-fill form data
        setSignupData((prev) => ({
          ...prev,
          email: inviteData.email || '',
          name: inviteData.companyName || '',
        }))
        setLoginData((prev) => ({
          ...prev,
          email: inviteData.email || '',
        }))

        // Simple logic: email has account? login : signup
        if (existingUserData) {
          setViewMode('login')
        } else {
          setViewMode('signup')
        }
      } catch (err: any) {
        const errorMsg = err.response?.data?.error || 'Convite nao encontrado'
        setError(errorMsg)
        setViewMode('error')
      } finally {
        setLoadingInvite(false)
      }
    }

    loadInvite()
  }, [token])

  function handleCNPJDataConfirmed(data: CNPJData) {
    setSignupData((prev) => ({
      ...prev,
      name: data.razao_social || data.nome_fantasia || prev.name,
      document: data.cnpj,
      phone: data.ddd_telefone_1 || prev.phone,
    }))
  }

  // Accept invite via login (existing user)
  async function handleAcceptWithLogin(e: React.FormEvent) {
    e.preventDefault()
    if (!token) return

    if (!loginData.password) {
      setError('Digite sua senha')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await api.post(`/agent-invite/${token}/accept-with-login`, {
        email: loginData.email,
        password: loginData.password,
      })

      // Set auth tokens - this replaces any existing tokens
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token)
        localStorage.setItem('refresh_token', response.data.refresh_token)
      }

      setViewMode('success')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao aceitar convite')
    } finally {
      setLoading(false)
    }
  }

  // Accept invite via signup (new user)
  async function handleAcceptWithSignup(e: React.FormEvent) {
    e.preventDefault()
    if (!token) return

    if (!signupData.email || !signupData.name) {
      setError('Preencha todos os campos obrigatorios')
      return
    }

    if (signupData.password.length < 6) {
      setError('A senha deve ter no minimo 6 caracteres')
      return
    }

    if (signupData.password !== signupData.confirmPassword) {
      setError('As senhas nao conferem')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await api.post(`/agent-invite/${token}/accept`, {
        email: signupData.email,
        name: signupData.name,
        document: signupData.document || undefined,
        phone: signupData.phone || undefined,
        password: signupData.password,
      })

      // Set auth tokens if returned - this replaces any existing tokens
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token)
        localStorage.setItem('refresh_token', response.data.refresh_token)
      }

      setViewMode('success')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao aceitar convite')
    } finally {
      setLoading(false)
    }
  }

  // Check if we got tokens from the accept operation
  const hasNewTokens = !!localStorage.getItem('access_token')

  // Password validation helpers
  const passwordMinLength = 6
  const isPasswordValid = signupData.password.length >= passwordMinLength
  const doPasswordsMatch = signupData.password === signupData.confirmPassword
  const showPasswordError = passwordTouched && signupData.password.length > 0 && !isPasswordValid
  const showConfirmPasswordError = confirmPasswordTouched && signupData.confirmPassword.length > 0 && !doPasswordsMatch

  // ==================== RENDER STATES ====================

  // Loading state
  if (viewMode === 'loading' || loadingInvite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-16 pb-16">
            <div className="text-center">
              <Spinner size={56} className="mx-auto text-emerald-600 mb-6 animate-spin" />
              <p className="text-gray-600 text-lg">Validando convite...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state
  if (viewMode === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-12 pb-12 px-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle size={48} className="text-red-500" weight="duotone" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Convite Invalido</h2>
              <p className="text-gray-600 mb-8">{error}</p>
              <Button onClick={() => navigate('/login')} variant="outline" className="h-12 px-8">
                Ir para Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Success state
  if (viewMode === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full shadow-xl border-0">
          <CardContent className="pt-12 pb-12 px-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={48} className="text-emerald-600" weight="duotone" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Convite Aceito com Sucesso!</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Voce agora e agente de carga de{' '}
                <span className="font-semibold text-gray-900">{invite?.shipperName}</span>.{' '}
                {hasNewTokens
                  ? 'Clique abaixo para acessar a plataforma.'
                  : 'Faca login para acessar as oportunidades de cotacao e enviar suas propostas.'}
              </p>

              <div className="bg-emerald-50 rounded-xl p-6 mb-8">
                <div className="flex items-center justify-center gap-8">
                  <div className="text-center">
                    <Package size={32} className="text-emerald-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Receba cotacoes</p>
                  </div>
                  <div className="text-center">
                    <Handshake size={32} className="text-emerald-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Envie propostas</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => {
                  // Always force full page reload to ensure fresh auth state
                  window.location.href = hasNewTokens ? '/freight-forwarder/dashboard' : '/login'
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold"
              >
                {hasNewTokens ? 'Ir para o Dashboard' : 'Acessar Plataforma'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ==================== MAIN VIEWS ====================

  // Login form (existing user)
  if (viewMode === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full shadow-xl border-0 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-8 py-8 text-center text-white">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
              <SignIn size={36} className="text-white" weight="duotone" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Faca Login para Aceitar</h1>
            <p className="text-emerald-100 text-sm">Voce ja tem uma conta na plataforma</p>
          </div>

          <CardContent className="p-8">
            {/* Invite Details */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 mb-6 border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Buildings size={24} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Convite de</p>
                  <p className="font-semibold text-gray-900 leading-tight">{invite?.shipperName}</p>
                </div>
              </div>
            </div>

            {/* Recognized user */}
            <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <User size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-blue-800 font-medium">{existingUser?.name}</p>
                  <p className="text-blue-700 text-sm">{existingUser?.email}</p>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-5 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm flex items-center gap-3">
                <XCircle size={20} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleAcceptWithLogin} className="space-y-5">
              <div>
                <Label htmlFor="email" className="flex items-center gap-2 text-gray-700 mb-2">
                  <EnvelopeSimple size={16} className="text-gray-400" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={loginData.email}
                  className="h-12 bg-gray-50"
                  disabled
                />
              </div>

              <div>
                <Label htmlFor="password" className="flex items-center gap-2 text-gray-700 mb-2">
                  <Lock size={16} className="text-gray-400" />
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData((prev) => ({ ...prev, password: e.target.value }))}
                    placeholder="Digite sua senha"
                    className="h-12 pr-12"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold"
              >
                {loading ? (
                  <>
                    <Spinner size={20} className="animate-spin mr-2" />
                    Entrando...
                  </>
                ) : (
                  <>
                    <SignIn size={20} className="mr-2" weight="bold" />
                    Entrar e Aceitar Convite
                  </>
                )}
              </Button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setViewMode('signup')}
                  className="text-sm text-gray-500 hover:text-emerald-600"
                >
                  Nao e voce? Criar nova conta
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Signup form (new user)
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-xl border-0 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-8 py-8 text-center text-white">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <UserPlus size={36} className="text-white" weight="duotone" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Criar Conta e Aceitar</h1>
          <p className="text-emerald-100 text-sm">Configure sua conta para aceitar o convite</p>
        </div>

        <CardContent className="p-8">
          {/* Invite Details */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 mb-6 border border-gray-100">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Buildings size={24} className="text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Convite de</p>
                <p className="font-semibold text-gray-900 leading-tight">{invite?.shipperName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 pt-3 border-t border-gray-100">
              <CalendarCheck size={18} className="text-emerald-600" />
              <span>
                Valido ate{' '}
                <strong className="text-gray-900">
                  {new Date(invite?.expiresAt || '').toLocaleDateString('pt-BR')}
                </strong>
              </span>
            </div>
          </div>

          {error && (
            <div className="mb-5 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm flex items-center gap-3">
              <XCircle size={20} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleAcceptWithSignup} className="space-y-5">
            <div>
              <Label htmlFor="name" className="flex items-center gap-2 text-gray-700 mb-2">
                <User size={16} className="text-gray-400" />
                Nome da Empresa
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={signupData.name}
                onChange={(e) => setSignupData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Razao social ou nome fantasia"
                className="h-12"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2 text-gray-700 mb-2">
                <EnvelopeSimple size={16} className="text-gray-400" />
                Email
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={signupData.email}
                className="h-12 bg-gray-50"
                required
                disabled
              />
              <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Email vinculado ao convite
              </p>
            </div>

            <CNPJLookupInput
              value={signupData.document}
              onChange={(value) => setSignupData((prev) => ({ ...prev, document: value }))}
              onDataConfirmed={handleCNPJDataConfirmed}
              label="CNPJ"
              placeholder="00.000.000/0000-00"
            />

            <div>
              <Label htmlFor="phone" className="flex items-center gap-2 text-gray-700 mb-2">
                <Phone size={16} className="text-gray-400" />
                Telefone
                <span className="text-gray-400 text-xs font-normal">(opcional)</span>
              </Label>
              <Input
                id="phone"
                value={signupData.phone}
                onChange={(e) => setSignupData((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="(00) 00000-0000"
                className="h-12"
              />
            </div>

            {/* Password section */}
            <div className="border-t border-gray-200 pt-5 mt-5">
              <p className="text-sm text-gray-600 mb-4">Crie uma senha para acessar a plataforma:</p>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="password" className="flex items-center gap-2 text-gray-700 mb-2">
                    <Lock size={16} className="text-gray-400" />
                    Senha
                    <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={signupData.password}
                      onChange={(e) => setSignupData((prev) => ({ ...prev, password: e.target.value }))}
                      onBlur={() => setPasswordTouched(true)}
                      placeholder="Minimo 6 caracteres"
                      className={`h-12 pr-20 ${showPasswordError ? 'border-red-500 focus-visible:ring-red-500' : ''} ${isPasswordValid && signupData.password.length > 0 ? 'border-emerald-500 focus-visible:ring-emerald-500' : ''}`}
                      required
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                      {isPasswordValid && signupData.password.length > 0 && (
                        <CheckCircle size={18} className="text-emerald-500" weight="fill" />
                      )}
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                  {showPasswordError && (
                    <p className="text-xs text-red-500 mt-1.5">A senha deve ter no minimo 6 caracteres</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="flex items-center gap-2 text-gray-700 mb-2">
                    <Lock size={16} className="text-gray-400" />
                    Confirmar Senha
                    <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={signupData.confirmPassword}
                      onChange={(e) =>
                        setSignupData((prev) => ({ ...prev, confirmPassword: e.target.value }))
                      }
                      onBlur={() => setConfirmPasswordTouched(true)}
                      placeholder="Repita a senha"
                      className={`h-12 pr-20 ${showConfirmPasswordError ? 'border-red-500 focus-visible:ring-red-500' : ''} ${doPasswordsMatch && isPasswordValid && signupData.confirmPassword.length > 0 ? 'border-emerald-500 focus-visible:ring-emerald-500' : ''}`}
                      required
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                      {doPasswordsMatch && isPasswordValid && signupData.confirmPassword.length > 0 && (
                        <CheckCircle size={18} className="text-emerald-500" weight="fill" />
                      )}
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                  {showConfirmPasswordError && (
                    <p className="text-xs text-red-500 mt-1.5">As senhas nao conferem</p>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-4 space-y-3">
              <Button
                type="submit"
                disabled={loading || !isPasswordValid || !doPasswordsMatch}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold shadow-lg shadow-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Spinner size={20} className="animate-spin mr-2" />
                    Processando...
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} className="mr-2" weight="bold" />
                    Criar Conta e Aceitar Convite
                  </>
                )}
              </Button>

              {existingUser && (
                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => setViewMode('login')}
                    className="text-sm text-gray-500 hover:text-emerald-600"
                  >
                    Ja tem conta? Fazer login
                  </button>
                </div>
              )}

              <Button
                type="button"
                onClick={() => navigate('/')}
                variant="ghost"
                className="w-full h-12 text-gray-500 hover:text-gray-700"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
